<template>
  <RevContainer class="pb-32 md:pb-56">
    <RevBreadcrumbs
      :ariaLabel="i18n(translations.breadcrumb)"
      :breads="breadcrumb"
      @click-breadcrumb="trackClickEvent('come_back_product_breadcrumb')"
    />

    <SeoText
      :body-text="pageDataConfig.seoTextBody"
      class="mt-32 md:mt-0"
      :hidden-body-text="pageDataConfig.seoTextHiddenBody"
      :links="pageDataConfig.seoTextLinks"
      :main-title="pageDataConfig.seoTextMainTitle"
    />

    <ReviewsBlock
      id="reviews"
      :ai-summary="undefined"
      :ai-summary-pending="false"
      :hide-main-title="true"
      :link="route.path"
      :next-reviews-cursor="reviewsResponse?.nextCursor"
      :pending-summary="ratingSummaryPending || pendingRatesResponse"
      :previous-reviews-cursor="reviewsResponse?.previousCursor"
      :product-id="String(route.params.uuid || '')"
      :rating="ratesResponse"
      :rating-summary
      :reviews
      :reviews-pending="isReviewsFetchPending"
    />
  </RevContainer>
</template>

<script setup lang="ts">
import {
  useHead,
  useRoute,
  useRouteLocationWithLocale,
  useRuntimeConfig,
} from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevBreadcrumbs } from '@ds/components/Breadcrumbs'
import { RevContainer } from '@ds/components/Container'

import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'
import { HOME } from '~/scopes/home/route-names'
import { useAddCanonicalLink } from '~/scopes/seo/composables/useAddCanonicalLink'
import { getMetas } from '~/utils/seo/meta'

import ReviewsBlock from '../components/ReviewsBlock/ReviewsBlock.vue'
import SeoText from '../components/SeoText/SeoText.vue'
import { useGlobalRating } from '../composables/useGlobalRating'
import { useRatingDetailsSummary } from '../composables/useRatingDetailsSummary'
import { useReviewsList } from '../composables/useReviewsList'
import { REVIEWS_PAGE_CONTENT_BY_ROUTE, TRACKING_ZONE } from '../constants'
import translations from '../translations/reviews.translations'

const route = useRoute()
const i18n = useI18n()
const { trackClick } = useTracking()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const {
  public: { COMPANY_NAME },
} = useRuntimeConfig()

const { ratesResponse, pendingRatesResponse } = useGlobalRating()
const { isReviewsFetchPending, reviewsResponse } = useReviewsList()
const { data: ratingSummary, pending: ratingSummaryPending } =
  useRatingDetailsSummary()

const reviews = computed(() => {
  return reviewsResponse.value?.results
})

const pageDataConfig = computed(
  () => REVIEWS_PAGE_CONTENT_BY_ROUTE[route.name?.toString() ?? ''],
)

useHead(() => ({
  title: i18n(pageDataConfig.value.seoTitle),
  meta: getMetas({
    title: i18n(pageDataConfig.value.seoTitle),
    titleTemplate: `%s | ${COMPANY_NAME}`,
    description: i18n(pageDataConfig.value.seoDescription, {
      rate: ratesResponse.value?.averageRate,
      count: ratesResponse.value?.count,
    }),
  }),
}))

useAddCanonicalLink()

const pageType = computed(() => {
  const routeName = route.name?.toString() || ''

  return TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName]
})

const breadcrumb = computed(() => {
  const { breadcrumbParent, breadcrumbCurrentTitle } = pageDataConfig.value

  return [
    {
      link: resolveLocalizedRoute({ name: HOME }),
      title: i18n(translations.breadcrumbHomePage),
    },
    ...insertIf(!!breadcrumbParent, [
      {
        link: resolveLocalizedRoute({ name: breadcrumbParent?.routeName }),
        title: breadcrumbParent?.title ? i18n(breadcrumbParent.title) : '',
      },
    ]),
    {
      link: route.path,
      title: i18n(breadcrumbCurrentTitle),
    },
  ]
})

function trackClickEvent(name: string) {
  trackClick({
    zone: TRACKING_ZONE,
    pageType: pageType.value,
    name,
  })
}
</script>
